// @ts-check
// pwa/lib/router.js — in-app navigation interception.
//
// Two paths in order of precedence (first one that fires wins):
//
//   1. Document-level click handler on same-origin <a href>. Always runs in
//      every browser. preventDefault + history.pushState + dispatch. Modifier
//      keys, target=_blank, download attr, cross-origin → fall through to
//      browser default (full reload, which is correct for those cases).
//
//   2. Navigation API `navigate` listener (Chrome/Edge, Firefox 147+,
//      Safari 26.2). Belt-and-braces for programmatic navigation
//      (history.pushState from elsewhere, browser back/forward) — same-origin
//      same-document. Calls intercept() with an ASYNC handler so the
//      Navigation API actually waits for the mount to render before treating
//      the navigation as complete.
//
// Before this rewrite, the wrapper was Navigation-API-only with a sync
// `handler: () => onNavigate(...)`. The handler returned void synchronously,
// so the Navigation API treated the navigation as complete *immediately* —
// before dispatch's async mount functions (mountAdminRoute does
// `await api.get('/api/auth/me')` then a dynamic import) finished rendering.
// User-visible symptom: URL changes, content stays stale, manual refresh
// works.
//
// Reference: https://web.dev/blog/baseline-navigation-api

/** @type {((pathname: string) => unknown) | null} */
let CALLBACK = null;

/**
 * Register a callback for in-app navigation. The callback receives the new
 * path+search (e.g. `/admin/sessions/new?course=BM`). May return a Promise;
 * the router awaits it where the platform supports waiting.
 *
 * Idempotent: subsequent calls replace the previous handler.
 *
 * @param {(pathname: string) => unknown} onNavigate
 */
export function watchInternalNavigation(onNavigate) {
  CALLBACK = onNavigate;

  // The Navigation API's `navigate` event fires for BOTH anchor clicks AND
  // history.pushState (WICG spec / Chrome docs). So when it's present it is the
  // SOLE interception path — adding a click handler that also calls pushState
  // would double-dispatch (the documented cause of our setHTML/lit null crashes).
  // `event.intercept()` commits the URL itself; we never call pushState here.
  const nav = /** @type {any} */ (globalThis).navigation;
  if (nav && typeof nav.addEventListener === 'function') {
    nav.addEventListener('navigate', handleNavigateEvent);
  } else {
    // Fallback ONLY for browsers without the Navigation API: manual click
    // interception (pushState does NOT fire navigate here, so no double-fire)
    // + popstate for back/forward.
    document.addEventListener('click', handleAnchorClick);
    window.addEventListener('popstate', () => {
      if (!CALLBACK) return;
      CALLBACK(location.pathname + location.search);
    });
  }
}

/** @param {MouseEvent} e */
function handleAnchorClick(e) {
  // Defer to browser for any modified click.
  if (e.defaultPrevented) return;
  if (e.button !== 0) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

  const target = /** @type {Element|null} */ (e.target);
  const anchor = /** @type {HTMLAnchorElement|null} */ (
    target && target.closest ? target.closest('a[href]') : null
  );
  if (!anchor) return;
  // Cross-target, downloads, framing attrs → browser default.
  if (anchor.target && anchor.target !== '_self') return;
  if (anchor.hasAttribute('download')) return;
  if (anchor.getAttribute('rel') && /(^|\s)external(\s|$)/.test(anchor.getAttribute('rel') || '')) return;

  const href = anchor.getAttribute('href') || '';
  // Empty, hash-only, mailto, tel, javascript: → browser default.
  if (!href || href.startsWith('#') || /^(mailto:|tel:|javascript:)/i.test(href)) return;

  const url = new URL(anchor.href, location.href);
  if (url.origin !== location.origin) return;

  // Same path AND same search AND no hash change → no-op (avoid re-mount).
  if (url.pathname === location.pathname && url.search === location.search && !url.hash) {
    e.preventDefault();
    return;
  }
  // Same page, new #fragment (the Console / Status jump panel) → the browser scrolls; no re-mount.
  if (url.pathname === location.pathname && url.search === location.search && url.hash) return;

  e.preventDefault();
  // pushState first so the URL bar updates before the async mount renders.
  history.pushState(null, '', url.pathname + url.search + url.hash);
  if (CALLBACK) {
    // Run asynchronously so the click handler returns quickly. Errors are
    // logged but don't break the URL update.
    Promise.resolve()
      .then(() => CALLBACK && CALLBACK(url.pathname + url.search))
      .catch((err) => {
        console.error('router: navigation render failed', err);
      });
  }
}

/** @param {any} event */
function handleNavigateEvent(event) {
  if (!event.canIntercept) return;
  if (event.hashChange) return;
  if (event.downloadRequest) return;
  if (event.navigationType === 'reload') return;

  const dest = new URL(event.destination.url);
  if (dest.origin !== location.origin) return;

  // /youth-board* is a SEPARATE app (yb-worker, its own Cloudflare route +
  // asset bundle — see yb/worker/wrangler.toml), reached via tab-nav.js's
  // rel="external" Youth Board link. But the Navigation API's `navigate` event
  // fires for same-origin anchor clicks regardless of `rel` — and per the
  // note above, this handler is the SOLE interception path in browsers that
  // support it (Chrome/Edge, Firefox 147+, Safari 26.2), so the click-handler
  // fallback's rel="external" check below never even runs there. Without this
  // check, the click was "handled" here by re-dispatching into the Hub's own
  // router, which has no /youth-board route — so the URL bar updated but the
  // previously-mounted view (e.g. Dashboard) stayed on screen. 2026-09-06:
  // this — not the Service Worker — is the real cause of the reported
  // "/youth-board/ doesn't load, needs a manual refresh" symptom (a manual
  // refresh works because a full page load never goes through this router at
  // all). Bail out so the browser performs a real navigation instead.
  if (dest.pathname === '/youth-board' || dest.pathname.startsWith('/youth-board/')) return;

  // Same path + search → no-op self-link (e.g. the admin seg-toggle's active
  // tab). Intercept with an empty handler so the browser doesn't full-reload,
  // but don't re-dispatch. Cross-path navs (e.g. /signup → /) fall through and
  // dispatch normally — that's the tab-nav re-render path.
  if (dest.pathname === location.pathname && dest.search === location.search && !dest.hash) {
    event.intercept({ handler: async () => {} });
    return;
  }

  // Async handler — the Navigation API awaits the returned Promise before
  // treating the navigation as complete. Critical: without `async`, the
  // handler returns undefined synchronously and the navigation completes
  // before the mount renders.
  event.intercept({
    handler: async () => {
      if (!CALLBACK) return;
      await CALLBACK(dest.pathname + dest.search);
    },
  });
}
